import React from 'react';
import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button';

type LegacyVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type LegacySize = 'sm' | 'md' | 'lg';

const LEGACY_VARIANTS = new Set<string>(['primary', 'secondary', 'outline', 'ghost', 'danger']);

const LEGACY_BASE_STYLES =
  'inline-flex items-center justify-center font-semibold rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-[0.98]';

const LEGACY_VARIANT_STYLES: Record<LegacyVariant, string> = {
  primary:
    'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 focus:ring-indigo-500 shadow-lg shadow-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/40 dark:from-indigo-500 dark:to-purple-500',
  secondary:
    'bg-slate-200 text-slate-900 hover:bg-slate-300 focus:ring-slate-500 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600',
  outline:
    'border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 focus:ring-indigo-500 dark:border-indigo-400 dark:text-indigo-400 dark:hover:bg-indigo-950/30',
  ghost: 'text-slate-700 hover:bg-slate-100 focus:ring-slate-500 dark:text-slate-300 dark:hover:bg-slate-800',
  danger:
    'bg-gradient-to-r from-rose-600 to-red-600 text-white hover:from-rose-700 hover:to-red-700 focus:ring-rose-500 shadow-lg shadow-rose-500/30',
};

const LEGACY_SIZE_STYLES: Record<LegacySize, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-2.5 text-sm',
  lg: 'px-8 py-3 text-base',
};

export interface AppButtonProps extends Omit<MuiButtonProps, 'size' | 'variant'> {
  /**
   * Accepts either vocabulary: this app's own hand-styled variants
   * (primary/secondary/outline/ghost/danger — used by every screen outside
   * Ticket Management) or MUI's native ones (text/outlined/contained — used
   * by Ticket Management). Defaults to 'contained', unchanged from before
   * this component absorbed the legacy vocabulary.
   */
  variant?: MuiButtonProps['variant'] | LegacyVariant;
  size?: MuiButtonProps['size'] | LegacySize;
  /** Legacy vocabulary only: shows a spinner and disables the button. */
  isLoading?: boolean;
  /** Legacy vocabulary only: leading icon. MUI vocabulary uses `startIcon` instead. */
  icon?: React.ReactNode;
}

/**
 * Shared button.
 *
 * Ticket Management passes MUI's own props (`variant="outlined"/"contained"/"text"`,
 * `startIcon`, `component`, `color`, `sx`, ...) and renders through MUI's `Button`,
 * exactly as this component always has.
 *
 * Every other screen passes the app's legacy vocabulary
 * (`primary`/`secondary`/`outline`/`ghost`/`danger`, `isLoading`, `icon`) and
 * renders through the app's own hand-styled Tailwind markup — the same markup
 * the app's old `components/ui/Button.tsx` used to own directly (since
 * retired), reused from here so both callers share one implementation
 * instead of two.
 */
export const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(
  ({ variant = 'contained', ...rest }, ref) => {
    if (typeof variant === 'string' && LEGACY_VARIANTS.has(variant)) {
      const {
        size = 'md',
        isLoading = false,
        icon,
        className = '',
        disabled,
        type = 'button',
        children,
        ...domProps
      } = rest as AppButtonProps & { children?: React.ReactNode };

      return (
        <button
          ref={ref}
          type={type as React.ButtonHTMLAttributes<HTMLButtonElement>['type']}
          className={`${LEGACY_BASE_STYLES} ${LEGACY_VARIANT_STYLES[variant as LegacyVariant]} ${LEGACY_SIZE_STYLES[size as LegacySize]} ${className}`}
          disabled={isLoading || disabled}
          {...(domProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading...
            </>
          ) : (
            <>
              {icon && <span className="mr-2">{icon}</span>}
              {children}
            </>
          )}
        </button>
      );
    }

    const { sx, ...muiProps } = rest as MuiButtonProps;
    return (
      <MuiButton
        ref={ref}
        variant={variant as MuiButtonProps['variant']}
        sx={{ borderRadius: 2, ...sx }}
        {...muiProps}
      />
    );
  }
);

AppButton.displayName = 'AppButton';

export default AppButton;
